import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Banknote, CreditCard, Download, Gift, Minus, Plus, Ticket, Trash2, Undo2, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { CreditSummary, CouponDialog } from "./WalkinCredits";
import { posFee } from "./usePosCharge";
import { WalkinBuyerPicker, EMAIL_RE } from "./WalkinBuyerPicker";

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

const creditsOf = (tickets) => tickets.filter(t => t.credit_issued).map(t => ({
  ticket_id: t.id, amount: t.on_attend_credit_amount, destination: t.credit_destination, coupon_code: t.credit_coupon_code, reversed: false,
}));

function TierPicker({ tiers, onAdd }) {
  if (!tiers.length) return <p className="text-sm text-slate-400 text-center py-6" data-testid="walkin-no-tiers">No tickets are valid today for this event.</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="walkin-tiers">
      {tiers.map(t => {
        const out = !t.sellable;
        const status = out ? t.blocked_message : `${t.walkin_left} of ${t.walkin_capacity} left`;
        return (
          <button key={t.id} disabled={out} onClick={() => onAdd(t)} data-testid={`walkin-tier-${t.id}`}
            className={`text-left rounded-xl border-2 p-3 transition-colors border-slate-200 bg-white ${out ? "opacity-40 grayscale cursor-not-allowed" : "hover:border-[#1e3a5f]/50"}`}>
            <p className="font-semibold text-[#1e3a5f] text-sm flex items-center justify-between gap-2">{t.name}{!out && <Plus className="w-4 h-4 shrink-0" />}</p>
            <p className="text-xs text-slate-500 mt-0.5">{eur(t.unit_price)}{t.service_charge > 0 && <span className="text-slate-400"> (incl. {eur(t.service_charge)} walk-in surcharge)</span>}</p>
            <p className={`text-xs font-bold mt-1 ${out ? "text-red-600" : "text-green-700"}`} data-testid={`walkin-left-${t.id}`}>{status}</p>
          </button>
        );
      })}
    </div>
  );
}

function BasketLine({ line, i, tier, max, onChange, onRemove }) {
  return (
    <div className="py-2 border-b last:border-0 space-y-1.5" data-testid={`walkin-line-${i}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-[#1e3a5f] truncate">{tier.name}</p>
          <p className="text-xs text-slate-500">{eur(tier.unit_price)} each{tier.on_attend_credit_amount > 0 ? ` · ${eur(tier.on_attend_credit_amount)} credit` : ""}</p>
        </div>
        {tier.variants.length > 0 && (
          <select value={line.variant_id} onChange={e => onChange({ variant_id: e.target.value })} className="max-w-[45%] px-2 py-1.5 rounded-lg border text-sm" data-testid={`walkin-line-variant-${i}`}>
            <option value="">Variant…</option>
            {tier.variants.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => onChange({ quantity: Math.max(1, line.quantity - 1) })} className="w-7 h-7 rounded-full border flex items-center justify-center" data-testid={`walkin-line-minus-${i}`}><Minus className="w-3.5 h-3.5" /></button>
        <span className="w-6 text-center font-bold text-sm" data-testid={`walkin-line-qty-${i}`}>{line.quantity}</span>
        <button onClick={() => onChange({ quantity: line.quantity + 1 })} disabled={line.quantity >= max} className="w-7 h-7 rounded-full border flex items-center justify-center disabled:opacity-30" data-testid={`walkin-line-plus-${i}`}><Plus className="w-3.5 h-3.5" /></button>
        <span className="ml-auto font-bold text-sm">{eur(tier.unit_price * line.quantity)}</span>
        <button onClick={onRemove} className="p-1 text-red-500" aria-label="Remove" data-testid={`walkin-line-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function SaleForm({ eventId, lines, setLines, tiers, busy, onSell, posRule }) {
  const [name, setName] = useState("");
  const [buyer, setBuyer] = useState(null);
  const [typed, setTyped] = useState("");
  const typedEmail = !buyer && EMAIL_RE.test(typed.trim()) ? typed.trim().toLowerCase() : "";
  const [method, setMethod] = useState("cash");
  const tierOf = (id) => tiers.find(t => t.id === id);
  const usedOf = (id, skip) => lines.reduce((n, l, j) => n + (l.ticket_type_id === id && j !== skip ? l.quantity : 0), 0);
  const qty = lines.reduce((n, l) => n + l.quantity, 0);
  const subtotal = lines.reduce((n, l) => n + (tierOf(l.ticket_type_id)?.unit_price || 0) * l.quantity, 0);
  const fee = method === "card" ? posFee(posRule, subtotal) : 0;
  const missingVariant = lines.some(l => tierOf(l.ticket_type_id)?.variants.length > 0 && !l.variant_id);
  const update = (i, patch) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const credit = lines.some(l => tierOf(l.ticket_type_id)?.on_attend_credit_amount > 0);
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm space-y-3" data-testid="walkin-sale-form">
      <p className="font-bold text-[#1e3a5f]">This sale</p>
      <div data-testid="walkin-basket">
        {lines.map((l, i) => {
          const t = tierOf(l.ticket_type_id);
          return t && <BasketLine key={l.key} line={l} i={i} tier={t} max={t.walkin_left - usedOf(t.id, i)} onChange={(p) => update(i, p)} onRemove={() => setLines(ls => ls.filter((_, j) => j !== i))} />;
        })}
      </div>
      {credit && <p className="text-xs text-green-700" data-testid="walkin-credit-hint">On-attend credit goes to the tagged member/guest wallet, otherwise as a coupon to the email</p>}
      <WalkinBuyerPicker eventId={eventId} value={buyer} onChange={setBuyer} onQueryChange={setTyped} />
      {typedEmail && <p className="text-[11px] text-amber-700 -mt-1" data-testid="walkin-typed-email-hint">Tickets will be emailed to {typedEmail}</p>}
      {!buyer?.name && (
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Attendee name (optional)" className="w-full px-3 py-2 rounded-xl border text-sm" data-testid="walkin-name-input" />
      )}
      <div className="grid grid-cols-2 gap-2">
        {[["cash", "Cash", Banknote], ["card", "Card", CreditCard]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setMethod(k)} data-testid={`walkin-method-${k}`}
            className={`py-2.5 rounded-xl border-2 text-sm font-semibold flex items-center justify-center gap-2 ${method === k ? "border-[#2d8a4e] bg-green-50 text-[#2d8a4e]" : "border-slate-200 text-slate-600"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      {fee > 0 && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="walkin-pos-fee">Tickets {eur(subtotal)} + payment processing charges {eur(fee)} · <strong>Buyer pays {eur(subtotal + fee)}</strong></p>}
      <button disabled={busy || !qty || missingVariant} data-testid="walkin-sell-btn"
        onClick={() => onSell({ items: lines.map(l => ({ ticket_type_id: l.ticket_type_id, variant_id: l.variant_id || null, quantity: l.quantity })), attendee_name: buyer?.name || name, email: buyer?.email || typedEmail,
          customer_kind: ["member", "guest"].includes(buyer?.kind) ? buyer.kind : null, customer_id: buyer?.id || null, payment_method: method })}
        className="w-full py-3 rounded-full bg-[#2d8a4e] text-white font-bold disabled:opacity-40">
        {busy ? "Selling…" : missingVariant ? "Choose a variant for each line" : `Collect ${eur(subtotal + fee)} (${method}) & issue ${qty} ticket(s)`}
      </button>
    </div>
  );
}

function SaleDone({ sale, onPdf, onNew }) {
  return (
    <div className="bg-green-50 border border-green-200 rounded-2xl p-4 space-y-2" data-testid="walkin-sale-done">
      <p className="font-bold text-green-800 flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Sold {sale.tickets.length} ticket(s) · {eur(sale.total)}{sale.pos_fee > 0 ? ` + ${eur(sale.pos_fee)} payment processing charges` : ""} · checked in</p>
      <p className="text-xs text-green-700">Order {sale.order_id}{sale.email_queued ? " · ticket emailed" : ""}</p>
      <CreditSummary credits={sale.credits} />
      <div className="flex flex-wrap gap-2">
        {sale.tickets.map(t => (
          <button key={t.id} onClick={() => onPdf(t.id)} className="text-xs px-3 py-1.5 rounded-full bg-white border flex items-center gap-1" data-testid={`walkin-pdf-${t.id}`}><Download className="w-3.5 h-3.5" /> Ticket {t.id.slice(0, 6)}</button>
        ))}
      </div>
      <button onClick={onNew} className="text-sm font-semibold text-[#1e3a5f] underline" data-testid="walkin-new-sale-btn">New sale</button>
    </div>
  );
}

function RecentSales({ sales, onVoid, onCoupons }) {
  if (!sales.length) return null;
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="walkin-recent-sales">
      <h3 className="font-bold text-sm text-[#1e3a5f] mb-2">Recent walk-in sales</h3>
      <div className="divide-y">
        {sales.map(s => (
          <div key={s.order_id} className="py-2 flex items-center gap-2 text-xs" data-testid={`walkin-sale-${s.order_id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-700 truncate">{(s.lines || [{ quantity: s.quantity, label: s.tier_name }]).map(l => `${l.quantity}× ${l.label}`).join(", ")}</p>
              <p className="text-slate-400">{fmtTime(s.sold_at)} · {s.payment_method} · {s.sold_by}{s.buyer_email ? ` · ${s.buyer_email}` : ""}</p>
            </div>
            <span className="font-bold">{eur(s.total)}{s.pos_fee > 0 && <span className="text-amber-600 font-normal"> +{eur(s.pos_fee)} processing</span>}</span>
            {s.credits?.some(c => c.destination === "coupon") && <button onClick={() => onCoupons(s)} className="flex items-center gap-1 text-[#1e3a5f] hover:underline" data-testid={`walkin-coupons-${s.order_id}`}><Gift className="w-3.5 h-3.5" /> Coupon</button>}
            {s.status === "cancelled" ? <span className="text-red-500 font-semibold">voided</span>
              : s.voidable && <button onClick={() => onVoid(s)} className="flex items-center gap-1 text-red-500 hover:underline" data-testid={`walkin-void-${s.order_id}`}><Undo2 className="w-3.5 h-3.5" /> Void</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WalkinSale({ eventId, onBack }) {
  const { authAxios } = useAuth();
  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sale, setSale] = useState(null);
  const [couponSale, setCouponSale] = useState(null);

  const load = () => authAxios.get(`/ticketing/accountant/events/${eventId}/walkin`).then(r => {
    setData(r.data);
  }).catch(e => toast.error(e.response?.data?.detail || "Failed to load"));
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line

  const sell = async (payload) => {
    setBusy(true);
    try {
      const r = await authAxios.post(`/ticketing/accountant/events/${eventId}/walkin/sell`, payload);
      setSale({ ...r.data, credits: creditsOf(r.data.tickets) }); setLines([]); toast.success("Tickets issued"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sale failed"); load(); } finally { setBusy(false); }
  };
  const addTier = (t) => {
    setSale(null);
    const used = lines.reduce((n, l) => n + (l.ticket_type_id === t.id ? l.quantity : 0), 0);
    if (used >= t.walkin_left) { toast.error(`No more walk-in seats left for ${t.name}`); return; }
    const same = lines.findIndex(l => l.ticket_type_id === t.id && !t.variants.length);
    if (same >= 0) setLines(lines.map((l, j) => (j === same ? { ...l, quantity: l.quantity + 1 } : l)));
    else setLines([...lines, { key: `${t.id}-${Date.now()}`, ticket_type_id: t.id, variant_id: "", quantity: 1 }]);
  };
  const pdf = async (id) => {
    try {
      const r = await authAxios.get(`/ticketing/accountant/walkin/tickets/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch { toast.error("Could not open ticket"); }
  };
  const voidSale = async (s) => {
    const credit = (s.credits || []).length ? " Any on-attend credit from this sale will be withdrawn." : "";
    if (!window.confirm(`Void ${s.quantity} ticket(s)? Hand back ${eur(s.total + (s.pos_fee || 0))} (${s.payment_method}) to the buyer.${credit}`)) return;
    try { await authAxios.post(`/ticketing/accountant/walkin/orders/${s.order_id}/void`); toast.success("Sale voided"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4" data-testid="walkin-sale">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500" data-testid="walkin-back-btn"><ArrowLeft className="w-4 h-4" /> Back to Event</button>
      <div className="flex items-center gap-2">
        <Ticket className="w-5 h-5 text-[#1e3a5f]" />
        <h2 className="font-bold text-lg text-[#1e3a5f]">Walk-in ticket sale</h2>
      </div>
      {data && <p className="text-xs text-slate-500">{data.event.name}{data.service_charge_pct > 0 && ` · walk-in surcharge ${data.service_charge_pct}%`} · payment collected offline (cash/card) · tickets are checked in at sale</p>}
      {!data ? <p className="text-slate-400 text-sm">Loading…</p> : (
        <>
          <p className="text-xs text-slate-500">Tap a ticket to add it to the sale — add several types to charge them together.</p>
          <TierPicker tiers={data.tiers} onAdd={addTier} />
          {sale ? <SaleDone sale={sale} onPdf={pdf} onNew={() => setSale(null)} />
            : lines.length > 0 && <SaleForm eventId={eventId} lines={lines} setLines={setLines} tiers={data.tiers} busy={busy} onSell={sell} posRule={data.pos_charge} />}
          <RecentSales sales={data.sales} onVoid={voidSale} onCoupons={setCouponSale} />
          <CouponDialog sale={couponSale} onClose={() => setCouponSale(null)} />
        </>
      )}
    </div>
  );
}
